import type { AuthTokens } from "@repo/types";

export interface TokenPersistenceAdapter {
  load: () => Promise<AuthTokens | null> | AuthTokens | null;
  save: (tokens: AuthTokens) => Promise<void> | void;
  clear: () => Promise<void> | void;
}

const isBrowser = typeof window !== "undefined";
const LOCAL_STORAGE_KEY = "aq1.auth.tokens";

const defaultBrowserAdapter: TokenPersistenceAdapter = {
  load: () => {
    if (!isBrowser) return null;
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthTokens;
    } catch (error) {
      console.warn("[tokenStorage] Failed to parse persisted tokens", error);
      window.localStorage.removeItem(LOCAL_STORAGE_KEY);
      return null;
    }
  },
  save: (tokens) => {
    if (!isBrowser) return;
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tokens));
  },
  clear: () => {
    if (!isBrowser) return;
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
};

class TokenStorage {
  private tokens: AuthTokens | null = null;
  private remember = false;
  private persistenceAdapter: TokenPersistenceAdapter | null = isBrowser
    ? defaultBrowserAdapter
    : null;
  private refreshHandler: (() => Promise<AuthTokens>) | null = null;

  setPersistenceAdapter(adapter: TokenPersistenceAdapter | null) {
    this.persistenceAdapter = adapter;
  }

  async loadPersistedTokens() {
    if (!this.persistenceAdapter) return null;
    const persisted = await this.persistenceAdapter.load();
    if (!persisted) return null;
    this.tokens = persisted;
    this.remember = true;
    return persisted;
  }

  getTokens() {
    return this.tokens;
  }

  getAccessToken() {
    return this.tokens?.accessToken ?? null;
  }

  async setTokens(tokens: AuthTokens, options?: { remember?: boolean }) {
    this.tokens = tokens;
    if (typeof options?.remember === "boolean") {
      this.remember = options.remember;
      if (!this.remember && this.persistenceAdapter) {
        await this.persistenceAdapter.clear();
      }
    }
    if (this.remember && this.persistenceAdapter) {
      await this.persistenceAdapter.save(tokens);
    }
  }

  async clearTokens() {
    this.tokens = null;
    if (this.persistenceAdapter) {
      await this.persistenceAdapter.clear();
    }
  }

  setRefreshHandler(handler: (() => Promise<AuthTokens>) | null) {
    this.refreshHandler = handler;
  }

  async refreshTokens() {
    if (!this.refreshHandler) {
      throw new Error("[tokenStorage] Refresh handler has not been registered");
    }
    const tokens = await this.refreshHandler();
    await this.setTokens(tokens);
    return tokens;
  }
}

export const tokenStorage = new TokenStorage();
